<!--
  A view behind its loading screen. While `phase` is `loading` it shows
  nothing at first, then the loading screen once the load has run for
  SHOW_AFTER_MS, for MIN_SHOWN_MS at least; a `failed` load shows the
  screen at once, with why; `ready` shows the view (loading/gate.ts, which
  decides all of that from the phase and the time). A view that is ready
  when it mounts never has a loading screen in the page. A gate inside
  another's view takes over from it when it mounts still loading: the
  screen that was up stays, as it was, until this load has run for the
  delay too, so the two are one wait and one screen.

  The polite live region is here from the start, so what the load is at is
  announced when it changes: the step under way, never its counts. The
  loading screen takes no focus; focus that was in it when the view took
  its place moves to where the view is.
-->
<script lang="ts" module>
  import { HANDOFF_MS, type Gate } from './gate';
  import type { LoadView } from './steps';

  /**
   * The gate that last opened onto its view, as it was before, when, and
   * the steps its loading screen showed (null: none was up). A view
   * mounting right then takes over from it.
   */
  let opened: { gate: Gate; at: number; view: LoadView | null } | null = null;
</script>

<script lang="ts">
  import { tick, untrack, type Snippet } from 'svelte';
  import { github } from '../api/gh';
  import type { Retrying } from '../api/scheduler';
  import { MIN_SHOWN_MS, open, showing, step, type GateMsg, type Phase } from './gate';
  import Loader from './Loader.svelte';
  import { announcement } from './steps';

  interface Props {
    phase: Phase;
    /** The load's steps; read only while the loading screen is shown. */
    view: LoadView;
    /** Try the stopped step again. */
    onretry?: () => void;
    /**
     * False where the view has loading gates of its own: this one opens
     * the moment its load is in, so theirs start at once, and they keep
     * the screen up for what is left of its minimum.
     */
    linger?: boolean;
    /** The view is a part of a page under its own header: its loading screen sits at the top of its space. */
    compact?: boolean;
    children: Snippet;
  }
  let { phase, view, onretry, linger = true, compact = false, children }: Props = $props();

  const born = Date.now();
  const left = opened && born >= opened.at && born - opened.at <= HANDOFF_MS ? opened : null;
  const first = open(
    untrack(() => phase),
    born,
    left?.gate,
  );
  /** The steps of the screen left up over this view, shown until the screen is this load's own. */
  const cover = left?.view ?? null;
  let covering = $state.raw(cover !== null);
  let gate: Gate = $state.raw(first[0]);
  /** When the gate wants to hear the time again. */
  let wake: number | null = $state.raw(first[1]);
  /** The live region: its parent is where the loading screen and then the view are. */
  let status: HTMLElement | undefined = $state();

  /**
   * Focus that was in the loading screen (its Try again) when the view
   * took its place goes to where the view is, not back to the top of the
   * document.
   */
  function keepFocus() {
    const region = status?.parentElement;
    const held = region?.querySelector('[data-loader]')?.contains(document.activeElement);
    if (!region || !held) return;
    void tick().then(() => {
      if (region.contains(document.activeElement) && document.activeElement !== region) return;
      if (!region.hasAttribute('tabindex')) region.setAttribute('tabindex', '-1');
      region.focus({ preventScroll: true });
    });
  }

  function send(m: GateMsg) {
    const before = gate;
    [gate, wake] = step(gate, m, untrack(() => linger) ? MIN_SHOWN_MS : 0);
    if (gate.at === 'shown') covering = false;
    if (gate.at !== 'open' || before.at === 'open') return;
    keepFocus();
    // What was on screen when it opened, for a view that mounts under it.
    const up = before.at === 'pending' ? null : covering ? cover : untrack(() => view);
    opened = { gate: before, at: m.now, view: up };
  }

  $effect.pre(() => {
    const p = phase;
    untrack(() => send({ t: 'phase', phase: p, now: Date.now() }));
  });
  $effect(() => {
    if (wake === null) return;
    const timer = setTimeout(() => send({ t: 'time', now: Date.now() }), Math.max(0, wake - Date.now()));
    return () => clearTimeout(timer);
  });

  const on = $derived(showing(gate));

  let retrying: Retrying | null = $state(null);
  $effect(() => github.onRetrying((r) => (retrying = r)));
</script>

<p class="sr-only" role="status" data-loader-status bind:this={status}>{on === 'loader' && !covering ? announcement(view) : ''}</p>
{#if (on === 'loader' || on === 'cover') && covering && cover}
  <Loader view={cover} {retrying} {compact} />
{:else if on === 'loader'}
  <Loader {view} {onretry} {retrying} {compact} since={born} />
{:else if phase === 'ready'}
  {@render children()}
{/if}
